// Pure text builders for the activation preflight. The enrollment sentence is shared with the
// trigger inspector's summary so the two surfaces cannot drift apart.
import { type Condition, conditionComplete, conditionScope, eventLabel, operatorLabel } from './conditions'
import { availableFieldsForTrigger, dateAnchorLabel, fieldLabelByKey, isDateOnlyAnchor } from './dataContext'
import { type GlobalWorkflowSettings, throttleSummary } from './globalSettings'
import { isDateAnchored } from './serialize'
import { contactSourceNoun, triggerDef } from './triggerCatalog'
import type { SendTimesSettings, TriggerNodeData, WorkflowNode } from 'app/types/workflows'
import { LEGAL_SEND_END, LEGAL_SEND_START, clampToLegalSendWindow, effectiveCategory } from './workflowSettings'

export type ValueChoices = Record<string, { value: string; label: string }[]>

export const CHANGE_SENTENCE: Record<string, string> = {
  is: `changes to`,
  is_not: `changes to anything but`,
  contains: `changes to text containing`,
  is_set: `gets any value`,
  is_empty: `is cleared`,
}

const valueLabel = (valueOptions: ValueChoices, field?: string, value?: string) =>
  (field && value && valueOptions[field]?.find((choice) => choice.value === value)?.label) || value || ``

export function conditionPhrase(
  c: Condition,
  scope: `change` | `state`,
  valueOptions: ValueChoices = {},
  fieldScope: string[] = [],
): string {
  if (c.type === `event`) return c.withinDays ? `${eventLabel(c.event)} within ${c.withinDays}d` : eventLabel(c.event)
  const label = fieldLabelByKey(c.field, fieldScope) ?? c.field ?? ``
  const value = valueLabel(valueOptions, c.field, c.value)
  return scope === `change`
    ? `${label} ${CHANGE_SENTENCE[c.operator]}${value ? ` ${value}` : ``}`.trim()
    : `${label} ${operatorLabel(c.operator)}${value ? ` ${value}` : ``}`.trim()
}

const UNIT_NAMES: Record<string, { singular: string; plural: string }> = {
  m: { singular: `minute`, plural: `minutes` },
  h: { singular: `hour`, plural: `hours` },
  d: { singular: `day`, plural: `days` },
  w: { singular: `week`, plural: `weeks` },
  mo: { singular: `month`, plural: `months` },
  y: { singular: `year`, plural: `years` },
}

/** One date-trigger moment as words: "30 days before", "1 hour after". For a date-only anchor
 *  (hygiene due date, birthday), "+1 hour after" is the ENCODING of "on the day" — the engine
 *  rejects zero offsets — so it reads back as the thing it means, not as its workaround. */
export function offsetPhrase(
  offset: { amount: number; unit: string; direction: `before` | `after` },
  dateField?: string,
): string {
  if (isDateOnlyAnchor(dateField) && offset.amount === 1 && offset.unit === `h` && offset.direction === `after`)
    return `on the day`
  const names = UNIT_NAMES[offset.unit] ?? { singular: offset.unit, plural: offset.unit }
  return `${offset.amount} ${offset.amount === 1 ? names.singular : names.plural} ${offset.direction}`
}

/** The full "what starts this" sentence. Mirrors the inspector's blue summary for event triggers,
 *  and speaks each trigger family's own language (schedule / manual) where that reads better. */
export function enrollmentSentence(data: TriggerNodeData, valueOptions: ValueChoices = {}): string {
  if (data.kind === `manual`)
    return `Nothing enrolls on its own. You add people from the contacts table or the Runs panel.`

  const conditions = data.conditions ?? []
  const fieldScope = availableFieldsForTrigger(data)
  const stateJoiner = (data.stateMatch ?? data.match) === `any` ? ` or ` : ` and `
  const stateText = conditions
    .filter((c) => conditionScope(c) !== `change`)
    .filter(conditionComplete)
    .map((c) => conditionPhrase(c, `state`, valueOptions, fieldScope))
    .join(stateJoiner)

  if (isDateAnchored(data.kind)) {
    const anchor = dateAnchorLabel(data.dateField) ?? fieldLabelByKey(data.dateField) ?? data.dateField ?? `a date`
    const moments = (data.dateOffsets ?? []).map((offset) => offsetPhrase(offset, data.dateField)).join(`, `)
    // For a date trigger EVERY user condition is checked against the live record at due time
    // (serialize routes them all to state filters), so they all read as "only if".
    const dateOnlyIf = conditions
      .filter(conditionComplete)
      .map((c) => conditionPhrase(c, `state`, valueOptions, fieldScope))
      .join(stateJoiner)
    return `Counts from ${anchor}. Sends at: ${moments || `no moments yet`}.${dateOnlyIf ? ` Only if ${dateOnlyIf} at that moment.` : ``}`
  }

  const def = triggerDef(data.kind)
  const sourceNoun = contactSourceNoun(data.kind)
  const enrolls = sourceNoun ? `Enrolls the ${sourceNoun}'s contact` : `Enrolls`
  const onlyIf = stateText ? `, but only if ${stateText}` : ``
  const changeJoiner = data.match === `any` ? ` or ` : ` and `
  const changeConditions = conditions.filter((c) => conditionScope(c) === `change`).filter(conditionComplete)

  // A created event writes its values rather than changing them, so "changes to" would misdescribe it.
  if (def?.lifecycle === `created`) {
    const noun = def.objectLabel.toLowerCase()
    const where = changeConditions.map((c) => conditionPhrase(c, `state`, valueOptions, fieldScope)).join(changeJoiner)
    return `${enrolls} when ${/^[aeiou]/.test(noun) ? `an` : `a`} ${noun} is created${where ? ` where ${where}` : ``}${onlyIf}.`
  }

  const changeText = changeConditions
    .map((c) => conditionPhrase(c, `change`, valueOptions, fieldScope))
    .join(changeJoiner)
  return `${enrolls} when ${changeText || (def?.blunt ? `the record is edited (any change at all)` : `the trigger event happens`)}${onlyIf}.`
}

/** The outbound count is load-bearing: it decides whether the send-window and caps lines apply. */
export function stepSummary(nodes: WorkflowNode[]): { text: string; hasOutbound: boolean } {
  const steps = nodes.filter((node) => node.type !== `trigger`)
  // A handoff sends a real text, so it must count here: the send-window and cap lines are gated on
  // hasOutbound, and counting only sendSms hid them (and claimed nothing sends) on handoff-only flows.
  const handoffs = steps.filter((node) => node.type === `aiAgentSms`).length
  const texts = steps.filter((node) => node.type === `sendSms`).length + handoffs
  const calls = steps.filter((node) => node.type === `initiateCall`).length
  const count = `${steps.length} step${steps.length === 1 ? `` : `s`}`
  if (!texts && !calls) return { text: `${count}. No texts or calls; nothing leaves the building.`, hasOutbound: false }
  const outbound = [
    texts ? `${texts} text${texts === 1 ? `` : `s`}` : ``,
    calls ? `${calls} call${calls === 1 ? `` : `s`}` : ``,
  ]
    .filter(Boolean)
    .join(` and `)
  // The count is openers only; the agent keeps answering after that.
  const agentNote = handoffs ? ` After the handoff the AI agent replies on its own.` : ``
  return { text: `${count}, including ${outbound}.${agentNote}`, hasOutbound: true }
}

const time12h = (value: string): string => {
  const [h, m] = value.split(`:`).map(Number)
  const suffix = h >= 12 ? `PM` : `AM`
  const hour = h % 12 === 0 ? 12 : h % 12
  return `${hour}:${String(m).padStart(2, `0`)} ${suffix}`
}

const DAY_NAMES: Record<string, string> = {
  mon: `Mon`,
  tue: `Tue`,
  wed: `Wed`,
  thu: `Thu`,
  fri: `Fri`,
  sat: `Sat`,
  sun: `Sun`,
}

/** The send-window statement. Texting outside 8:00 AM to 8:00 PM local time is illegal and the
 *  engine refuses it; since api #587 a refused send HOLDS until the window opens rather than being
 *  dropped, so no window is a scheduling choice, not a loss. */
export function sendWindowSummary(sendTimes: SendTimesSettings | undefined): { text: string; warn: boolean } {
  if (!sendTimes?.restrict)
    return {
      text: `No send window: texts go out as soon as steps reach them, and anything landing outside the legal 8:00 AM to 8:00 PM hours holds until the window opens.`,
      warn: false,
    }
  const days =
    sendTimes.days.length >= 7
      ? `every day`
      : sendTimes.days.length === 0
        ? `on no days (nothing will ever send)`
        : `on ${sendTimes.days.map((day) => DAY_NAMES[day] ?? day).join(`, `)}`
  // Show the EFFECTIVE window: the engine refuses the overhang, so claiming it would be a lie.
  const start = clampToLegalSendWindow(sendTimes.start, LEGAL_SEND_START)
  const end = clampToLegalSendWindow(sendTimes.end, LEGAL_SEND_END)
  const clamped = start !== sendTimes.start || end !== sendTimes.end
  return {
    text: `Texts only send between ${time12h(start)} and ${time12h(end)}, ${days}, on the location's clock.${clamped ? ` (Your window reached outside the legal 8:00 AM to 8:00 PM texting hours and is capped to them.)` : ``}`,
    warn: sendTimes.days.length === 0 || clamped,
  }
}

/** The consent statement. The engine treats every workflow as marketing (consent-gated via
 *  Message.send) unless an ADMIN marks it transactional, and only an admin's save applies that —
 *  so the sentence has to describe what this user's save will actually produce, not what the
 *  picker says. `serverCategory` is what the engine holds today; absent means marketing. */
export function consentSummary(
  settings: { category?: `transactional` | `marketing` } | undefined,
  options: { canSetCategory?: boolean; serverCategory?: string | null } = {},
): { text: string; warn: boolean } {
  const chosen = settings?.category ?? `marketing`
  // What the engine will hold once this save lands: the choice only reaches it via an admin.
  const { category: effective } = effectiveCategory({ ...options, settings })

  if (effective === `marketing`)
    return {
      text:
        chosen === `transactional`
          ? `These send as marketing texting: only contacts who opted in receive them. Transactional is a Trackable admin setting, so ask a Trackable admin to save this workflow if these are appointment or care messages.`
          : `Marketing texting: only contacts who opted in to marketing texts receive these. Everyone else is skipped at send time.`,
      warn: chosen === `transactional`,
    }
  return {
    text: `Transactional texting (appointments and care): no marketing-consent check applies.`,
    warn: false,
  }
}

/** Caps inform rather than gate: absence is called out, not blocked. */
export function capsSummary(caps: GlobalWorkflowSettings): { text: string; warn: boolean } {
  if (caps.monthlyCap == null && caps.dailyCap == null)
    return { text: `No daily or monthly cap is set for this workspace.`, warn: true }
  return { text: `Workspace caps: ${throttleSummary(caps)}.`, warn: false }
}
